import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { auth } from '../firebase';

// ---------------------------------------------------------------------------
// Client Dashboards — staff overview (Master ▸ Client Dashboards, Oct 2026)
//
// One row per client with the dashboard switched on: who can sign in and
// when they last did, which month is live and what it is waiting on, chat
// use against the cap, open content requests, and a "View as client" link
// that opens dashboard.core6.marketing as that client sees it (logged).
// Everything comes from one staff-only endpoint in the dashboard codebase
// (functions-dashboard/lib/admin.js); the Hub reads no client rows itself.
// Last activity is touched at most hourly while someone is in the app, so it
// says the day they were last in, not how long they stayed.
// ---------------------------------------------------------------------------

const BASE = 'https://us-central1-core6-marketing.cloudfunctions.net';
const DASHBOARD = 'https://dashboard.core6.marketing';
const DAY = 86400000;

async function api(path, body) {
  const token = await auth.currentUser.getIdToken();
  const r = await fetch(`${BASE}/${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
  const data = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
  return data;
}

const fmtDate = ms => (ms ? new Date(ms).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');
function ago(ms, now) {
  if (!ms) return 'never';
  const d = Math.floor((now - ms) / DAY);
  if (d <= 0) return 'today';
  if (d === 1) return 'yesterday';
  if (d < 30) return `${d} days ago`;
  const mo = Math.floor(d / 30);
  return mo === 1 ? 'a month ago' : `${mo} months ago`;
}

const SORTS = {
  quiet: { label: 'Quietest first', fn: (a, b) => (a.lastLogin?.at || 0) - (b.lastLogin?.at || 0) || a.name.localeCompare(b.name) },
  recent: { label: 'Most recent login', fn: (a, b) => (b.lastLogin?.at || 0) - (a.lastLogin?.at || 0) || a.name.localeCompare(b.name) },
  name: { label: 'Client name', fn: (a, b) => a.name.localeCompare(b.name) },
  month: { label: 'Latest month', fn: (a, b) => String(b.latest?.month || '').localeCompare(String(a.latest?.month || '')) || a.name.localeCompare(b.name) },
};

function Pill({ on, children }) {
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '.4px', textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, whiteSpace: 'nowrap',
      ...(on ? { background: 'var(--text-primary)', color: 'var(--bg-primary, #fff)', border: '1px solid var(--text-primary)' } : { background: 'transparent', color: 'var(--text-muted)', border: '1px dashed var(--text-muted)' }),
    }}>{on ? '● ' : '○ '}{children}</span>
  );
}

const th = { textAlign: 'left', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.3px', color: 'var(--text-muted)', padding: '8px 10px', borderBottom: '1px solid var(--border-color, #e3e6ea)', whiteSpace: 'nowrap' };
const td = { fontSize: 13, padding: '10px 10px', borderBottom: '1px solid var(--border-color, #eef0f3)', verticalAlign: 'top' };

export default function DashboardAdmin() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState('quiet');
  const [filter, setFilter] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try { setData(await api('dashboardAdminOverview', {})); }
    catch (err) { setError(err.message || 'Could not load.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const now = Date.now();
  const rows = (data?.clients || [])
    .filter(c => !filter || c.name.toLowerCase().includes(filter.toLowerCase()) || c.users.some(u => (u.email || '').toLowerCase().includes(filter.toLowerCase())))
    .sort(SORTS[sort].fn);
  const never = (data?.clients || []).filter(c => !c.lastLogin).length;
  const noUsers = (data?.clients || []).filter(c => c.activeUsers === 0).length;
  const drafts = (data?.clients || []).filter(c => c.latest && c.latest.status === 'draft').length;

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h1 style={{ margin: 0 }}>Client Dashboards</h1>
          <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            {data ? `${data.clients.length} client${data.clients.length === 1 ? '' : 's'} with the dashboard on · ${never} never signed in · ${noUsers} with no active users · ${drafts} on a draft month · chat counts are for ${data.month}` : 'Who has signed in, which month is live, and a way in as each client.'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="Filter by client or email" style={{ fontSize: 12, padding: '6px 10px', border: '1px solid var(--border-color, #d6d9de)', borderRadius: 6 }} />
          <select value={sort} onChange={e => setSort(e.target.value)} style={{ fontSize: 12, padding: '6px 8px' }}>
            {Object.entries(SORTS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <button type="button" className="btn btn-secondary" style={{ fontSize: 12 }} onClick={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error && <div className="card" style={{ fontSize: 13, fontWeight: 600 }}>✕ {error}</div>}
      {!error && loading && !data && <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Loading every client's dashboard…</div>}
      {data && rows.length === 0 && <div className="card" style={{ fontSize: 13, color: 'var(--text-muted)' }}>{filter ? 'No client matches that filter.' : 'No client has the dashboard switched on yet. Turn it on from a client profile (Client Dashboard Sources).'}</div>}

      {rows.length > 0 && (
        <div className="card" style={{ padding: 0, overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 980 }}>
            <thead><tr>
              <th style={th}>Client</th><th style={th}>Latest month</th><th style={th}>AI</th><th style={th}>Users</th><th style={th}>Last client login</th><th style={th}>Logins · 30d</th><th style={th}>Chat</th><th style={th}>Requests</th><th style={th}>Staff last viewed</th><th style={th}></th>
            </tr></thead>
            <tbody>
              {rows.map(c => (
                <tr key={c.id}>
                  <td style={td}>
                    <Link to={`/clients/${c.id}`} style={{ fontWeight: 700, color: 'var(--text-primary)', textDecoration: 'underline', textUnderlineOffset: 2 }}>{c.name}</Link>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{c.sources.length ? c.sources.join(' · ') : 'no sources mapped'}</div>
                  </td>
                  <td style={td}>
                    {c.latest ? (
                      <>
                        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}><span style={{ fontWeight: 600 }}>{c.latest.periodLabel}</span><Pill on={c.latest.status === 'published'}>{c.latest.status === 'published' ? 'Published' : 'Draft'}</Pill></div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>
                          {c.latest.status === 'published' ? `published ${fmtDate(c.latest.publishedAt)}` : c.latest.missing.length ? `waiting on ${c.latest.missing.join(', ')}` : 'ready to publish'}
                          {c.latest.pending && c.latest.pending.length ? ` · early view, ${c.latest.pending.join(', ')} by ${c.latest.pendingBy}` : ''}
                          {c.latest.focusPending ? ' · last month\'s plan items unmarked' : ''}
                          {` · ${c.published} published, ${c.drafts} draft`}
                        </div>
                      </>
                    ) : <span style={{ color: 'var(--text-muted)' }}>No month assembled</span>}
                  </td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    {c.ai ? <><div style={{ fontWeight: 600 }}>{c.ai.named} of {c.ai.answers}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.ai.month}</div></> : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                  </td>
                  <td style={td}>
                    {c.users.length === 0 && <span style={{ color: 'var(--text-muted)' }}>No users{c.pendingInvites ? ` · ${c.pendingInvites} invite pending` : ''}</span>}
                    {c.users.map(u => (
                      <div key={u.uid} style={{ whiteSpace: 'nowrap', opacity: u.status === 'active' ? 1 : 0.55 }}>
                        <span style={{ fontWeight: 600 }}>{u.email}</span>
                        <span style={{ fontSize: 11, color: 'var(--text-muted)' }}> · {u.role}{u.status !== 'active' ? ` · ${u.status}` : ''} · {u.lastActiveAt ? `active ${ago(u.lastActiveAt, now)}` : 'never signed in'}</span>
                      </div>
                    ))}
                    {c.users.length > 0 && c.pendingInvites > 0 && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.pendingInvites} invite{c.pendingInvites === 1 ? '' : 's'} pending</div>}
                  </td>
                  <td style={td}>
                    <div style={{ fontWeight: c.lastLogin ? 600 : 400, color: c.lastLogin ? 'inherit' : 'var(--text-muted)' }}>{c.lastLogin ? ago(c.lastLogin.at, now) : 'Never'}</div>
                    {c.lastLogin && <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{fmtDate(c.lastLogin.at)} · {c.lastLogin.email}</div>}
                  </td>
                  <td style={{ ...td, textAlign: 'center', fontWeight: 600 }}>{c.logins30d}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{c.chatThisMonth} / {c.chatCap}</td>
                  <td style={{ ...td, textAlign: 'center' }}>{c.openRequests ? <strong>{c.openRequests} open</strong> : <span style={{ color: 'var(--text-muted)' }}>0</span>}</td>
                  <td style={td}>{c.lastViewAs ? <><div>{ago(c.lastViewAs.at, now)}</div><div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{c.lastViewAs.email}</div></> : <span style={{ color: 'var(--text-muted)' }}>—</span>}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>
                    <a className="btn btn-primary" style={{ fontSize: 12 }} href={`${DASHBOARD}/?viewAs=${encodeURIComponent(c.id)}${c.latest ? `&month=${c.latest.month}` : ''}`} target="_blank" rel="noreferrer">View as client ↗</a>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 10 }}>Loaded {new Date(data.generatedAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}. "Active" is touched at most once an hour while someone is in the app. Every View as client is logged on the client's dashboard.</div>}
    </div>
  );
}
